import React, { useState, useEffect } from 'react';
import { UserManagementService } from '@/services/user-management.service';
import { UserProfile, UserRole, AccountStatus } from '@/models/user.model';
import { Button, Card, EmptyState, LoadingSpinner, SectionHeader, Badge, FormInput, FormSelect } from '@/components/common';
import { Users, Search, Filter, User, CheckCircle2, Clock, Building2, Shield, Stethoscope, Mail, Phone, MapPin, MoreVertical } from 'lucide-react';

const UserRoleLabels: Record<UserRole, string> = {
  [UserRole.member]: 'Member',
  [UserRole.employerAdmin]: 'Employer Admin',
  [UserRole.faircareAdmin]: 'FairCare Admin',
  [UserRole.reviewer]: 'Reviewer',
  [UserRole.provider]: 'Provider',
};

const AccountStatusLabels: Record<AccountStatus, string> = {
  [AccountStatus.active]: 'Active',
  [AccountStatus.inactive]: 'Inactive',
  [AccountStatus.suspended]: 'Suspended',
  [AccountStatus.pendingVerification]: 'Pending Verification',
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<AccountStatus | 'all'>('all');

  useEffect(() => {
    const unsubscribe = UserManagementService.subscribeToUsers((data) => {
      setUsers(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        u.email.toLowerCase().includes(query) ||
        u.firstName?.toLowerCase().includes(query) ||
        u.lastName?.toLowerCase().includes(query) ||
        u.displayName?.toLowerCase().includes(query)
      );
    }

    // Apply role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(u => u.role === filterRole);
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(u => u.status === filterStatus);
    }

    setFilteredUsers(filtered);
  }, [users, searchQuery, filterRole, filterStatus]);

  const handleUpdateStatus = async (user: UserProfile, newStatus: AccountStatus) => {
    try {
      await UserManagementService.updateUserStatus(user.userId, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update user status');
    }
  };

  const handleUpdateRole = async (user: UserProfile, newRole: UserRole) => {
    if (!confirm(`Change ${user.email}'s role to ${UserRoleLabels[newRole]}?`)) {
      return;
    }
    try {
      await UserManagementService.updateUserRole(user.userId, newRole);
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update user role');
    }
  };

  // Calculate stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === AccountStatus.active).length,
    pending: users.filter(u => u.status === AccountStatus.pendingVerification).length,
    byRole: {
      members: users.filter(u => u.role === UserRole.member).length,
      employerAdmins: users.filter(u => u.role === UserRole.employerAdmin).length,
      faircareAdmins: users.filter(u => u.role === UserRole.faircareAdmin).length,
      providers: users.filter(u => u.role === UserRole.provider).length,
    },
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner text="Loading users..." fullScreen />;
  }

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    ...Object.entries(UserRoleLabels).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    ...Object.entries(AccountStatusLabels).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={Users}
        title="User Management"
        description="Manage all users across the platform"
      />

      {/* Stats Cards */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Members</p>
                <p className="text-3xl font-bold text-purple-600">{stats.byRole.members}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Employer Admins</p>
                <p className="text-3xl font-bold text-indigo-600">{stats.byRole.employerAdmins}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">FairCare Admins</p>
                <p className="text-3xl font-bold text-red-600">{stats.byRole.faircareAdmins}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Providers</p>
                <p className="text-3xl font-bold text-teal-600">{stats.byRole.providers}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormInput
            label="Search Users"
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
          />
          <FormSelect
            label="Role"
            options={roleOptions}
            value={filterRole.toString()}
            onChange={(e) => setFilterRole(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as UserRole)}
          />
          <FormSelect
            label="Status"
            options={statusOptions}
            value={filterStatus.toString()}
            onChange={(e) => setFilterStatus(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as AccountStatus)}
          />
        </div>
      </Card>

      {/* User List */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Users ({filteredUsers.length})
          </h3>
        </div>
        {filteredUsers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users found"
            description={
              searchQuery || filterRole !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'No users in the system'
            }
          />
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <UserCard
                  key={user.userId}
                  user={user}
                  onUpdateStatus={handleUpdateStatus}
                  onUpdateRole={handleUpdateRole}
                />
              ))}
            </div>
          )}
      </Card>
    </div>
  );
};


interface UserCardProps {
  user: UserProfile;
  onUpdateStatus: (user: UserProfile, status: AccountStatus) => void;
  onUpdateRole: (user: UserProfile, role: UserRole) => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, onUpdateStatus, onUpdateRole }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  const getStatusVariant = (status: AccountStatus): "success" | "warning" | "danger" | "gray" => {
    switch (status) {
      case AccountStatus.active:
        return 'success';
      case AccountStatus.inactive:
        return 'gray';
      case AccountStatus.suspended:
        return 'danger';
      case AccountStatus.pendingVerification:
        return 'warning';
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {user.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={user.displayName || user.email}
                className="w-12 h-12 rounded-lg"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-lg font-semibold text-gray-900">
                {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
              </h4>
              <Badge variant={getStatusVariant(user.status)} size="sm">
                {AccountStatusLabels[user.status]}
              </Badge>
              <Badge variant="info" size="sm">
                {UserRoleLabels[user.role]}
              </Badge>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
              <Mail className="w-4 h-4" />
              {user.email}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              {user.phoneNumber && (
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {user.phoneNumber}
                </span>
              )}
              {user.city && user.state && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {user.city}, {user.state}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <div className="relative">
            <Button
              variant={user.status === AccountStatus.active ? "success" : "outline"}
              size="sm"
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              gradient={false}
            >
              Change Status
            </Button>
            {showStatusMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowStatusMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-20 border border-gray-200">
                  {Object.entries(AccountStatusLabels).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => {
                        onUpdateStatus(user, parseInt(value) as AccountStatus);
                        setShowStatusMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        user.status === parseInt(value) ? 'text-blue-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="relative">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowRoleMenu(!showRoleMenu)}
              gradient={false}
            >
              Change Role
            </Button>
            {showRoleMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowRoleMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-20 border border-gray-200">
                  {Object.entries(UserRoleLabels).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => {
                        onUpdateRole(user, parseInt(value) as UserRole);
                        setShowRoleMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        user.role === parseInt(value) ? 'text-blue-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

